import React, { useState, useMemo, useCallback } from "react";
import { getStickyStockTableStickyColumnCss } from "utils/reactTableTheme";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  Table,
} from "reactstrap";
import { useGetEquipmentOptions, useDeleteEquipmentOption } from "api/equipment/service";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { useTranslation } from "react-i18next";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";

const formatEquipmentType = (type) => {
  if (!type) return "-";
  return type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

const Equipments = ({ permission, activeColor = "secondary", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: equipmentData, isLoading } = useGetEquipmentOptions();
  const deleteEquipmentMutation = useDeleteEquipmentOption();
  
  // Modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const handleDeleteClick = useCallback((id) => {
    setSelectedDeleteId(id);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = () => {
    if (selectedDeleteId) {
      deleteEquipmentMutation.mutate(selectedDeleteId, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setSelectedDeleteId(null);
        }
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  const equipmentRows = useMemo(() => {
    const list = equipmentData?.results || [];
    return list.map((item, idx) => ({
      id: item.documentId,
      documentId: item.documentId,
      item,
      row_number: idx + 1,
      type_display: formatEquipmentType(item.type),
      option_status: item.option_status,
    }));
  }, [equipmentData]);

  const equipmentTableColumns = useMemo(
    () => [
      { Header: t("equipments.number"), accessor: "row_number" },
      { Header: t("equipments.type"), accessor: "type_display" },
      {
        Header: t("equipments.status"),
        accessor: "option_status",
        disableSortBy: true,
        Cell: ({ value }) => (
          <span className={`badge badge-${value === "Active" ? "success" : "warning"}`}>
            {value === "Active" ? t("equipments.active") : value}
          </span>
        ),
      },
      {
        id: "actions",
        Header: "",
        accessor: "documentId",
        disableSortBy: true,
        Cell: ({ row }) => {
          const item = row.original.item;
          return (
            <div className="actions-right" style={{ textAlign: "right" }}>
              {permission?.inventory_parameters_equipments_edit && (
                <Button
                  color="link"
                  size="sm"
                  className="btn-icon btn-link like table-edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/equipments/edit/${item.documentId}`);
                  }}
                >
                  <i className="tim-icons icon-pencil" />
                </Button>
              )}
              {permission?.inventory_parameters_equipments_delete && (
                <Button
                  color="link"
                  size="sm"
                  className="btn-icon btn-link like table-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(item.documentId);
                  }}
                  disabled={deleteEquipmentMutation.isPending}
                >
                  {deleteEquipmentMutation.isPending && selectedDeleteId === item.documentId ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  ) : (
                    <i className="tim-icons icon-simple-remove" />
                  )}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [
      t,
      permission?.inventory_parameters_equipments_delete,
      permission?.inventory_parameters_equipments_edit,
      navigate,
      handleDeleteClick,
      deleteEquipmentMutation.isPending,
      selectedDeleteId,
    ]
  );

  const EquipmentsTable = shellThemeVariant === "2" ? ReactTable2 : ReactTable;

  return (
    <div className="tab-content">
      <style>
          {`
          .sticky-stock-table .rt-table {
            width: 100%;
            min-width: 100%;
            border-collapse: collapse;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) {
            text-align: center !important;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child {
            text-align: right !important;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) .rt-resizable-header-content {
            text-align: center;
            width: 100%;
            box-sizing: border-box;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child).-cursor-pointer .rt-resizable-header-content {
            padding-right: 2em;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child .rt-resizable-header-content {
            text-align: right;
            width: 100%;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) {
            text-align: center;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:last-child {
            text-align: right;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) > * {
            max-width: 100%;
            min-width: 0;
          }
            ${getStickyStockTableStickyColumnCss("")}
        `}
      </style>
      <Row>
        <Col md="12">
          <Card>
            <CardHeader className="equipments-card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h2 className="mb-0">{t('routes.inventoryParameters')}</h2>
                {permission?.inventory_parameters_equipments_add && (
                <Button 
                  size="sm" 
                  style={{ ...activeColorStyle, color: 'white' }}
                  onClick={() => navigate('/equipments/add')}
                >
                  {t('equipments.addEquipment')}
                </Button>
                )}
              </div>
            </CardHeader>
            <CardBody>
              <div
                className="inventory-table-wrap"
                style={{ fontSize: "13px", minWidth: 0, maxWidth: "100%" }}
              >
                <EquipmentsTable
                  activeColor={activeColor}
                  data={equipmentRows}
                  columns={equipmentTableColumns}
                  className="-striped -highlight sticky-stock-table"
                  manualPagination={false}
                  pageSize={20}
                  loading={isLoading}
                  loadingText={t("equipments.loading")}
                  noDataText={t("equipments.noEquipmentFound")}
                  onRowClick={(row) => {
                    if (row?.documentId) {
                      navigate(`/equipments/edit/${row.documentId}`);
                    }
                  }}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      
      {/* Delete Modal */}
      <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete} centered={false} style={{ marginTop: '100px' }}>
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t('equipments.deleteEquipment')}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t('equipments.deleteConfirmation')}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button 
            color="secondary" 
            type="button" 
            onClick={handleCancelDelete}
            disabled={deleteEquipmentMutation.isPending}
          >
            {t('equipments.cancel')}
          </Button>
          <Button 
             style={{
              background: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              backgroundImage: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              borderColor: '#fd5d93',
              color: 'white'
          }}
            color="danger" 
            type="button" 
            onClick={handleConfirmDelete}
            disabled={deleteEquipmentMutation.isPending}
          >
            {deleteEquipmentMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                {t('equipments.deleting')}
              </>
            ) : (
              t('equipments.yesDelete')
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Equipments; 